/**
 * Softphone view — agent-facing WebRTC dialer.
 *
 * Layout:
 *   [ dialpad column ][ Cerebro embed zone (placeholder for now)            ]
 *
 * PBX users (the MVP default): logged-in => available. No activity selector.
 * ACD machinery (queue/activity/aux codes) lives in a separate view once
 * acdEnabled goes live on the user.
 *
 * Phone input is forgiving: accepts +19188147589, 19188147589, (918) 814-7589.
 * Displayed in (AAA) XXX-YYYY. US-only for MVP.
 */

import { useEffect, useMemo, useState } from "react";
import { Phone, PhoneOff, Mic, MicOff, ArrowRightLeft } from "lucide-react";
import type { AppRole } from "../lib/roles";
import { Softphone, type SoftphoneState } from "../lib/softphone";
import { formatPhoneInput, formatE164ForDisplay, parseUSPhone } from "../lib/phone";
import { TransferModal } from "./TransferModal";

type SoftphoneViewProps = {
  me: {
    id: string;
    email: string;
    role: AppRole;
    directDialNumber?: string | null;
  };
};

export function SoftphoneView({ me }: SoftphoneViewProps) {
  const softphone = useMemo(() => new Softphone(), []);
  const [state, setState] = useState<SoftphoneState>({ kind: "idle" });
  const [input, setInput] = useState("");
  // Caller-id picker. Defaults to the user's directDialNumber; expands later
  // to a list of shared numbers the user is authorized to use.
  const availableFromNumbers = useMemo(
    () => (me.directDialNumber ? [me.directDialNumber] : []),
    [me.directDialNumber],
  );
  const [fromNumber, setFromNumber] = useState<string | null>(me.directDialNumber ?? null);
  const [showTransfer, setShowTransfer] = useState(false);

  useEffect(() => {
    const unsub = softphone.subscribe(setState);
    void softphone.start();
    return () => {
      unsub();
      softphone.shutdown();
    };
  }, [softphone]);

  const parsed = parseUSPhone(input);
  const display = input ? formatPhoneInput(input) : "";
  const callable = state.kind === "ready" && parsed.ok;
  const onCall =
    state.kind === "in-call" ||
    state.kind === "ringing-out" ||
    state.kind === "ringing-in" ||
    state.kind === "wrapping";

  return (
    <div className="h-full flex">
      {/* Dialpad column */}
      <div className="w-[clamp(20rem,32vmin,28rem)] shrink-0 pad-fluid flex flex-col gap-fluid border-r border-rogue-200">
        <div className="flex items-baseline justify-between">
          <h1 className="text-2xl font-bold text-rogue-900">Softphone</h1>
          <PresenceDot state={state} />
        </div>

        {/* Caller-id picker. Single option (their own number) for now;
            will surface shared/group numbers when those are wired. */}
        <div className="bg-white rounded-xl shadow pad-fluid">
          <label className="block text-xs uppercase tracking-wide text-rogue-500 mb-1">
            Outbound caller ID
          </label>
          {availableFromNumbers.length > 0 ? (
            <select
              value={fromNumber ?? ""}
              onChange={(e) => setFromNumber(e.target.value || null)}
              disabled={onCall}
              className="w-full text-lg font-mono text-rogue-900 bg-rogue-50 border border-rogue-200 rounded-lg px-3 py-2 focus:border-rogue-700 outline-none"
            >
              {availableFromNumbers.map((n) => (
                <option key={n} value={n}>{formatE164ForDisplay(n)}</option>
              ))}
            </select>
          ) : (
            <div className="text-sm text-rogue-500 italic">
              No outbound number assigned yet — operations manager needs to bind one to your account.
            </div>
          )}
        </div>

        {/* In-call action row: transfer (more buttons later — hold/conference/etc) */}
        {onCall && state.kind === "in-call" && (
          <div className="bg-white rounded-xl shadow pad-fluid flex gap-2">
            <button
              onClick={() => setShowTransfer(true)}
              className="flex-1 bg-rogue-700 hover:bg-rogue-800 text-white rounded-lg py-2 flex items-center justify-center gap-2 text-sm font-medium"
            >
              <ArrowRightLeft className="w-4 h-4" /> Transfer
            </button>
          </div>
        )}

        <SoftphoneStateBanner state={state} />

        <div className="bg-white rounded-xl shadow pad-fluid">
          <input
            value={display}
            onChange={(e) => setInput(e.target.value)}
            placeholder="(918) 814-7589"
            inputMode="tel"
            autoComplete="off"
            className="w-full text-2xl font-mono border-b-2 border-rogue-200 focus:border-rogue-700 outline-none pb-2 mb-1"
            disabled={onCall}
          />
          <div className="text-xs text-rogue-500 mb-3 h-4">
            {input && !parsed.ok && <span className="text-amber-700">{parsed.reason}</span>}
            {input && parsed.ok && <span>→ {parsed.e164}</span>}
          </div>

          <div className="grid grid-cols-3 gap-2">
            {["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"].map((k) => (
              <button
                key={k}
                onClick={() => setInput((n) => n + k)}
                disabled={onCall}
                className="bg-rogue-100 hover:bg-rogue-200 disabled:opacity-50 rounded-lg py-3 text-xl font-semibold text-rogue-900"
              >
                {k}
              </button>
            ))}
          </div>

          <div className="flex gap-2 mt-4">
            {state.kind === "ringing-in" ? (
              <>
                <button
                  onClick={() => softphone.accept()}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg py-3 flex items-center justify-center gap-2 font-medium"
                >
                  <Phone className="w-5 h-5" /> Answer
                </button>
                <button
                  onClick={() => softphone.hangup()}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-lg py-3 flex items-center justify-center gap-2 font-medium"
                >
                  <PhoneOff className="w-5 h-5" /> Decline
                </button>
              </>
            ) : !onCall ? (
              <button
                onClick={() => void softphone.call(input, fromNumber)}
                disabled={!callable}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white rounded-lg py-3 flex items-center justify-center gap-2 font-medium"
              >
                <Phone className="w-5 h-5" /> Call
              </button>
            ) : (
              <>
                <button
                  onClick={() => {
                    if (state.kind === "in-call") softphone.mute(!state.muted);
                  }}
                  disabled={state.kind !== "in-call"}
                  className={[
                    "bg-rogue-100 hover:bg-rogue-200 disabled:opacity-50 rounded-lg py-3 px-4 flex items-center gap-2",
                    state.kind === "in-call" && state.muted ? "bg-amber-100 text-amber-900" : "",
                  ].join(" ")}
                >
                  {state.kind === "in-call" && state.muted ? (
                    <MicOff className="w-5 h-5" />
                  ) : (
                    <Mic className="w-5 h-5" />
                  )}
                </button>
                <button
                  onClick={() => softphone.hangup()}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-lg py-3 flex items-center justify-center gap-2 font-medium"
                >
                  <PhoneOff className="w-5 h-5" /> Hang up
                </button>
              </>
            )}
          </div>
        </div>

        <div className="text-xs text-rogue-500">
          Signed in as <span className="font-medium">{me.email}</span>. PBX users are available
          while signed in; sign out to stop receiving calls.
        </div>
      </div>

      {/* Transfer modal */}
      {showTransfer && state.kind === "in-call" && (
        <TransferModal
          callSid={(state.call.parameters as Record<string, string> | undefined)?.["CallSid"] ?? ""}
          onClose={() => setShowTransfer(false)}
          onTransferred={() => setShowTransfer(false)}
        />
      )}

      {/* Cerebro / right-side embed zone */}
      <div className="flex-1 grid place-items-center pad-fluid">
        <div className="text-center max-w-md text-rogue-500">
          <div className="text-sm uppercase tracking-wide font-semibold text-rogue-400 mb-2">
            Reserved
          </div>
          <h2 className="text-lg text-rogue-700 font-medium mb-2">Cerebro mounts here</h2>
          <p className="text-sm">
            Caller context, CRM record, and AI assist will render in this panel as a cross-origin
            iframe in a future build.
          </p>
        </div>
      </div>
    </div>
  );
}

function PresenceDot({ state }: { state: SoftphoneState }): JSX.Element {
  const ready = state.kind === "ready" || state.kind === "in-call" ||
                state.kind === "ringing-out" || state.kind === "ringing-in" ||
                state.kind === "wrapping";
  return (
    <span className="flex items-center gap-2 text-xs text-rogue-600">
      <span
        className={[
          "inline-block w-2.5 h-2.5 rounded-full",
          ready ? "bg-emerald-500" : state.kind === "error" ? "bg-red-500" : "bg-rogue-300",
        ].join(" ")}
      />
      {ready ? "Available" : state.kind === "error" ? "Error" : "Connecting…"}
    </span>
  );
}

function SoftphoneStateBanner({ state }: { state: SoftphoneState }): JSX.Element | null {
  if (state.kind === "ready" || state.kind === "idle") return null;

  const palette: Partial<Record<SoftphoneState["kind"], string>> = {
    initializing: "bg-rogue-100 text-rogue-700",
    "ringing-out": "bg-emerald-100 text-emerald-800",
    "ringing-in": "bg-emerald-100 text-emerald-800",
    "in-call": "bg-emerald-100 text-emerald-800",
    wrapping: "bg-amber-100 text-amber-800",
    error: "bg-red-100 text-red-800",
  };

  let label = "";
  switch (state.kind) {
    case "initializing": label = "Connecting…"; break;
    case "ringing-out":  label = `Calling ${state.to}…`; break;
    case "ringing-in":   label = `Incoming from ${state.from}`; break;
    case "in-call":      label = `In call with ${state.remote}`; break;
    case "wrapping":     label = "Call ended"; break;
    case "error":        label = `Error: ${state.message}`; break;
  }
  return (
    <div className={["rounded-lg pad-fluid text-sm", palette[state.kind] ?? ""].join(" ")}>
      {label}
    </div>
  );
}
